import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import investorsService, {
  Investor,
  InvestorInput,
  InvestorOpportunityStage,
  InvestorType
} from '../../services/investorCrm/investorsService';
import activitiesService, {
  InvestorActivity,
  InvestorActivityInput
} from '../../services/investorCrm/activitiesService';
import {
  INVESTOR_ACTIVITY_OPTIONS,
  INVESTOR_STAGE_OPTIONS,
  INVESTOR_TYPE_OPTIONS
} from './constants';

interface InvestorDrawerProps {
  organizationId: string | null;
  investorId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

const INITIAL_ACTIVITY: Omit<InvestorActivityInput, 'organization_id' | 'investor_id'> = {
  activity_type: 'note',
  summary: '',
  outcome: '',
  next_step: '',
  occurred_at: undefined,
};

const InvestorDrawer: React.FC<InvestorDrawerProps> = ({
  organizationId,
  investorId,
  isOpen,
  onClose,
  onUpdated,
}) => {
  const [loadingInvestor, setLoadingInvestor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loggingActivity, setLoggingActivity] = useState(false);
  const [investor, setInvestor] = useState<Investor | null>(null);
  const [activities, setActivities] = useState<InvestorActivity[]>([]);
  const [activityForm, setActivityForm] = useState(() => ({ ...INITIAL_ACTIVITY }));
  const [isMounted, setIsMounted] = useState(false);

  const canEdit = useMemo(() => Boolean(organizationId && investorId), [organizationId, investorId]);
  const dialogTitleId = useMemo(
    () => (investorId ? `investor-drawer-title-${investorId}` : 'investor-drawer-title'),
    [investorId]
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const fetchInvestor = useCallback(async () => {
    if (!organizationId || !investorId || !isOpen) {
      setInvestor(null);
      setActivities([]);
      return;
    }

    setLoadingInvestor(true);
    try {
      const [{ data }, activityResponse] = await Promise.all([
        investorsService.get(investorId, organizationId),
        activitiesService.list({ organization_id: organizationId, investor_id: investorId, limit: 100, offset: 0 })
      ]);
      setInvestor(data as Investor | null);
      setActivities(activityResponse.data || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load investor profile');
    } finally {
      setLoadingInvestor(false);
    }
  }, [investorId, isOpen, organizationId]);

  useEffect(() => {
    void fetchInvestor();
  }, [fetchInvestor]);

  const updateField = (field: keyof InvestorInput, value: unknown) => {
    if (!investor) return;
    setInvestor({ ...investor, [field]: value });
  };

  const saveProfile = async () => {
    if (!organizationId || !investorId || !investor) return;
    const orgId = organizationId;
    setSaving(true);
    try {
      const payload: Partial<InvestorInput> & { organization_id: string } = {
        organization_id: orgId,
        company_name: investor.company_name,
        contact_name: investor.contact_name,
        contact_email: investor.contact_email,
        contact_phone: investor.contact_phone,
        opportunity_stage: investor.opportunity_stage,
        opportunity_summary: investor.opportunity_summary,
        notes: investor.notes,
        investor_type: investor.investor_type,
        firm_website: investor.firm_website,
        fund_focus: investor.fund_focus,
        headquarters_location: investor.headquarters_location,
        check_size_min: investor.check_size_min,
        check_size_max: investor.check_size_max,
        fund_size_notes: investor.fund_size_notes,
        institutional_investor: investor.institutional_investor,
        institutional_investor_type: investor.institutional_investor_type,
        institutional_investor_website: investor.institutional_investor_website,
      };

      const response = await investorsService.update(investorId, payload);
      setInvestor(response.data);
      toast.success('Investor profile updated');
      onUpdated?.();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save investor profile');
    } finally {
      setSaving(false);
    }
  };

  const logActivity = async () => {
    if (!organizationId || !investorId) return;
    if (!activityForm.summary) {
      toast.error('Summary is required');
      return;
    }

    setLoggingActivity(true);

    try {
      const payload: InvestorActivityInput = {
        organization_id: organizationId,
        investor_id: investorId,
        activity_type: activityForm.activity_type,
        summary: activityForm.summary,
        outcome: activityForm.outcome || undefined,
        next_step: activityForm.next_step || undefined,
        occurred_at: activityForm.occurred_at || undefined,
      };

      const response = await activitiesService.create(payload);
      setActivities((prev) => [response.data, ...prev]);
      setActivityForm({ ...INITIAL_ACTIVITY });
      toast.success('Activity logged');
      onUpdated?.();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to log activity');
    } finally {
      setLoggingActivity(false);
    }
  };

  const resetDrawerState = useCallback(() => {
    setInvestor(null);
    setActivities([]);
    setActivityForm({ ...INITIAL_ACTIVITY });
  }, []);

  const handleClose = useCallback(() => {
    resetDrawerState();
    onClose();
  }, [onClose, resetDrawerState]);

  useEffect(() => {
    if (!isOpen) {
      resetDrawerState();
    }
  }, [isOpen, resetDrawerState]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isMounted || !isOpen) {
    return null;
  }

  const content = (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby={dialogTitleId}>
      <button
        type="button"
        aria-hidden="true"
        aria-label="Close investor drawer"
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
      />
      <div className="absolute inset-y-0 right-0 z-10 flex h-full w-full max-w-3xl">
        <div className="flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div>
              <h2 id={dialogTitleId} className="text-lg font-semibold text-gray-900">
                {loadingInvestor ? 'Loading investor…' : investor?.company_name ?? 'Investor details'}
              </h2>
              {investor?.contact_name && (
                <p className="text-sm text-gray-500">Primary contact: {investor.contact_name}</p>
              )}
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-full border border-gray-200 px-3 py-1 text-sm text-gray-600 hover:border-gray-300 hover:text-gray-900"
            >
              Close
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            {loadingInvestor ? (
              <div className="py-20 text-center text-sm text-gray-500">Loading investor details…</div>
            ) : investor ? (
              <div className="space-y-8">
                <section className="space-y-4">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-700">Institutional profile</h2>
                    <p className="text-xs text-gray-500">Edit investor details including fund focus and check sizes.</p>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Company name</label>
                      <input
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.company_name}
                        onChange={(event) => updateField('company_name', event.target.value)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Investor type</label>
                      <select
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.investor_type ?? 'individual'}
                        onChange={(event) => updateField('investor_type', event.target.value as InvestorType)}
                        disabled={!canEdit}
                      >
                        {INVESTOR_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Firm website</label>
                      <input
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.firm_website ?? ''}
                        onChange={(event) => updateField('firm_website', event.target.value)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Headquarters</label>
                      <input
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.headquarters_location ?? ''}
                        onChange={(event) => updateField('headquarters_location', event.target.value)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Fund focus</label>
                      <textarea
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        rows={3}
                        value={investor.fund_focus ?? ''}
                        onChange={(event) => updateField('fund_focus', event.target.value)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Check size min (£)</label>
                      <input
                        type="number"
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.check_size_min ?? ''}
                        onChange={(event) => updateField('check_size_min', event.target.value ? Number(event.target.value) : null)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Check size max (£)</label>
                      <input
                        type="number"
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.check_size_max ?? ''}
                        onChange={(event) => updateField('check_size_max', event.target.value ? Number(event.target.value) : null)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Fund size notes</label>
                      <textarea
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        rows={2}
                        value={investor.fund_size_notes ?? ''}
                        onChange={(event) => updateField('fund_size_notes', event.target.value)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={Boolean(investor.institutional_investor)}
                          onChange={(event) => updateField('institutional_investor', event.target.checked)}
                          disabled={!canEdit}
                        />
                        Institutional investor
                      </label>
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Institution type</label>
                      <input
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.institutional_investor_type ?? ''}
                        onChange={(event) => updateField('institutional_investor_type', event.target.value)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Institution website</label>
                      <input
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.institutional_investor_website ?? ''}
                        onChange={(event) => updateField('institutional_investor_website', event.target.value)}
                        disabled={!canEdit}
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Primary contact</label>
                      <input
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.contact_name ?? ''}
                        onChange={(event) => updateField('contact_name', event.target.value)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contact email</label>
                      <input
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.contact_email ?? ''}
                        onChange={(event) => updateField('contact_email', event.target.value)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contact phone</label>
                      <input
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.contact_phone ?? ''}
                        onChange={(event) => updateField('contact_phone', event.target.value)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Stage</label>
                      <select
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={investor.opportunity_stage ?? 'prospecting'}
                        onChange={(event) => updateField('opportunity_stage', event.target.value as InvestorOpportunityStage)}
                        disabled={!canEdit}
                      >
                        {INVESTOR_STAGE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notes</label>
                    <textarea
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      rows={3}
                      value={investor.notes ?? ''}
                      onChange={(event) => updateField('notes', event.target.value)}
                      disabled={!canEdit}
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={saveProfile}
                      disabled={saving || !canEdit}
                      className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-gray-200"
                    >
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </section>

                <section className="space-y-4">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-700">Log activity</h2>
                    <p className="text-xs text-gray-500">Capture interactions with this investor.</p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Activity type</label>
                      <select
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={activityForm.activity_type}
                        onChange={(event) => setActivityForm((form) => ({ ...form, activity_type: event.target.value as InvestorActivityInput['activity_type'] }))}
                        disabled={loggingActivity}
                      >
                        {INVESTOR_ACTIVITY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Occurred at</label>
                      <input
                        type="datetime-local"
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={activityForm.occurred_at ?? ''}
                        onChange={(event) => setActivityForm((form) => ({ ...form, occurred_at: event.target.value || undefined }))}
                        disabled={loggingActivity}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Summary</label>
                    <textarea
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      rows={2}
                      value={activityForm.summary}
                      onChange={(event) => setActivityForm((form) => ({ ...form, summary: event.target.value }))}
                      disabled={loggingActivity}
                    />
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Outcome</label>
                      <textarea
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        rows={2}
                        value={activityForm.outcome ?? ''}
                        onChange={(event) => setActivityForm((form) => ({ ...form, outcome: event.target.value }))}
                        disabled={loggingActivity}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Next step</label>
                      <textarea
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        rows={2}
                        value={activityForm.next_step ?? ''}
                        onChange={(event) => setActivityForm((form) => ({ ...form, next_step: event.target.value }))}
                        disabled={loggingActivity}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={logActivity}
                      disabled={loggingActivity || !activityForm.summary.trim()}
                      className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-gray-200"
                    >
                      {loggingActivity ? 'Logging…' : 'Log activity'}
                    </button>
                  </div>
                </section>

                <section className="space-y-4">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-700">Activity timeline</h2>
                    <p className="text-xs text-gray-500">Chronological record of interactions with this investor.</p>
                  </div>
                  {activities.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-xs text-gray-400">
                      No activities logged yet.
                    </div>
                  ) : (
                    <ul className="space-y-4">
                      {activities.map((activity) => (
                        <li key={activity.id} className="rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-800">
                                {INVESTOR_ACTIVITY_OPTIONS.find((option) => option.value === activity.activity_type)?.label ?? activity.activity_type}
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(activity.occurred_at ?? activity.created_at).toLocaleString()}
                              </p>
                            </div>
                            <span className="text-xs text-gray-400">{activity.created_by}</span>
                          </div>
                          <p className="mt-3 text-sm text-gray-700 whitespace-pre-line">{activity.summary}</p>
                          {activity.outcome && (
                            <p className="mt-2 text-xs text-gray-500">
                              <span className="font-semibold text-gray-700">Outcome:</span> {activity.outcome}
                            </p>
                          )}
                          {activity.next_step && (
                            <p className="mt-1 text-xs text-gray-500">
                              <span className="font-semibold text-gray-700">Next step:</span> {activity.next_step}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            ) : (
              <div className="py-20 text-center text-sm text-gray-500">
                Select an investor to see details.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(content, document.body);
};

export default InvestorDrawer;
